import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { federation, api } from '../../utils/api';
import { useAuth } from '../../utils/AuthContext';
import FederationGate from './FederationGate';
import { ErrorBanner, LoadingBlock, PageHeader, btnPrimary, canWrite, inputClass, safeText } from './federationShared';

const emptyForm = {
  id: null,
  programId: '',
  teamId: '',
  reportingPeriodId: '',
  title: '',
  summary: '',
  fieldValues: {},
  status: 'Submitted',
};

function buildSummaryWithTemplate(summary, templateFields, templateAnswers, activeTemplate) {
  const summaryParts = [summary.trim()];
  if (Object.keys(templateAnswers).length) {
    summaryParts.push('\n--- Template responses ---');
    for (const field of templateFields) {
      summaryParts.push(`${field.label || field.key}: ${templateAnswers[field.key] || '—'}`);
    }
  }
  if (activeTemplate?.templateJson?.evidenceExpectations) {
    summaryParts.push(`\nEvidence expectations: ${activeTemplate.templateJson.evidenceExpectations}`);
  }
  return summaryParts.join('\n');
}

function SubmissionsInner() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const [programs, setPrograms] = useState([]);
  const [teams, setTeams] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [programTeams, setProgramTeams] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reviewNotes, setReviewNotes] = useState({});
  const [form, setForm] = useState(emptyForm);

  const activeTemplate = useMemo(
    () => (templates || []).find((t) => String(t.status || '').toLowerCase() === 'active') || null,
    [templates]
  );
  const templateFields = Array.isArray(activeTemplate?.templateJson?.fields)
    ? activeTemplate.templateJson.fields
    : [];

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      federation.getSubmissions(),
      federation.getPrograms(),
      api.getTeams(),
      federation.getPeriods(),
      federation.getTemplates(),
    ])
      .then(([s, p, t, periodsRes, templatesRes]) => {
        setSubmissions(s.submissions || []);
        setPrograms(p.programs || []);
        setTeams(t.teams || []);
        setPeriods(periodsRes.periods || periodsRes.reportingPeriods || []);
        setTemplates(templatesRes.templates || []);
      })
      .catch((e) => setError(e.message || 'Could not load submissions'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!form.programId) {
      setProgramTeams([]);
      return;
    }
    federation.getProgramTeams(form.programId)
      .then((res) => setProgramTeams(res.programTeams || res.memberships || res.teams || []))
      .catch(() => setProgramTeams([]));
  }, [form.programId]);

  const participatingTeamOptions = useMemo(() => {
    const assignedIds = new Set(
      (programTeams || []).map((row) => row.teamId || row.team_id || row.id).filter(Boolean)
    );
    if (!assignedIds.size) return teams;
    return teams.filter((team) => assignedIds.has(team.id));
  }, [programTeams, teams]);

  const openCreate = () => {
    setForm(emptyForm);
    setShowForm(true);
    setError('');
  };

  const openRevise = (submission) => {
    const answers = submission.templateAnswers && typeof submission.templateAnswers === 'object'
      ? submission.templateAnswers
      : {};
    setForm({
      id: submission.id,
      programId: submission.programId || '',
      teamId: submission.teamId || '',
      reportingPeriodId: submission.reportingPeriodId || '',
      title: submission.title || '',
      // Prefer plain summary without the appended template block when revising
      summary: String(submission.summary || '').split('\n--- Template responses ---')[0].trim(),
      fieldValues: answers,
      status: 'Submitted',
    });
    setShowForm(true);
    setError('');
  };

  const validateTemplate = () => {
    for (const field of templateFields) {
      if (!field.required) continue;
      const value = String(form.fieldValues?.[field.key] || '').trim();
      if (!value) {
        setError(`Required template field missing: ${field.label || field.key}`);
        return false;
      }
    }
    return true;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.programId || !form.title.trim() || !form.summary.trim()) {
      setError('Program, title, and summary are required');
      return;
    }
    if (!validateTemplate()) return;
    setSaving(true);
    setError('');
    try {
      const templateAnswers = templateFields.length
        ? Object.fromEntries(
          templateFields.map((field) => [field.key, String(form.fieldValues?.[field.key] || '').trim()])
        )
        : {};
      const summary = buildSummaryWithTemplate(
        form.summary,
        templateFields,
        templateAnswers,
        activeTemplate
      );
      const status = form.status === 'Draft' ? 'Draft' : 'Submitted';
      const payload = {
        programId: form.programId,
        teamId: form.teamId || null,
        reportingPeriodId: form.reportingPeriodId || null,
        title: form.title.trim(),
        summary,
        status,
        templateId: activeTemplate?.id || null,
        templateAnswers,
      };

      if (form.id) {
        await federation.updateSubmission({
          id: form.id,
          title: payload.title,
          summary: payload.summary,
          status: payload.status,
          templateId: payload.templateId,
          templateAnswers: payload.templateAnswers,
        });
      } else {
        await federation.createSubmission(payload);
      }
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (e) {
      setError(e.message || 'Could not save submission');
    } finally {
      setSaving(false);
    }
  };

  const reviewSubmission = async (submission, status) => {
    setError('');
    try {
      await federation.updateSubmission({
        id: submission.id,
        status,
        leadershipNotes: reviewNotes[submission.id] || submission.leadershipNotes || '',
      });
      load();
    } catch (e) {
      setError(e.message || 'Could not update submission');
    }
  };

  const isEditable = (status) => status === 'Draft' || status === 'Changes requested';
  const isReviewable = (status) => !['Accepted', 'Changes requested', 'Draft'].includes(status);

  return (
    <>
      <PageHeader
        title="Program submissions"
        description="Standardized program updates submitted for technical-leadership review."
        actions={writable ? (
          <button
            className={btnPrimary}
            type="button"
            onClick={() => {
              if (showForm) {
                setShowForm(false);
                setForm(emptyForm);
              } else {
                openCreate();
              }
            }}
          >
            {showForm ? 'Cancel' : 'New submission'}
          </button>
        ) : null}
      />
      <ErrorBanner message={error} onRetry={load} />
      {showForm && (
        <form onSubmit={submit} className="border border-navy-200 rounded-xl bg-white p-5 mb-6 space-y-4 max-w-xl">
          {form.id && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Revising submission — update the content and resubmit for leadership review.
            </p>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Program</label>
            <select
              className={inputClass}
              value={form.programId}
              onChange={(e) => setForm((f) => ({ ...f, programId: e.target.value, teamId: '' }))}
              required
              disabled={!!form.id}
            >
              <option value="">Select program</option>
              {programs.map((p) => <option key={p.id} value={p.id}>{safeText(p.name)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Participating team</label>
            <select
              className={inputClass}
              value={form.teamId}
              onChange={(e) => setForm((f) => ({ ...f, teamId: e.target.value }))}
              disabled={!!form.id}
            >
              <option value="">Organization-wide (optional)</option>
              {participatingTeamOptions.map((t) => <option key={t.id} value={t.id}>{safeText(t.name)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Reporting period</label>
            <select
              className={inputClass}
              value={form.reportingPeriodId}
              onChange={(e) => setForm((f) => ({ ...f, reportingPeriodId: e.target.value }))}
              disabled={!!form.id}
            >
              <option value="">Select period (optional)</option>
              {periods.map((p) => <option key={p.id} value={p.id}>{safeText(p.name)}</option>)}
            </select>
          </div>
          {activeTemplate ? (
            <div className="rounded-lg border border-navy-100 bg-slate-50 p-3 text-sm text-gray-700">
              Active template: <span className="font-medium text-navy-900">{safeText(activeTemplate.name)}</span>
              {activeTemplate.templateJson?.evidenceExpectations && (
                <p className="mt-1 text-xs text-gray-500">{safeText(activeTemplate.templateJson.evidenceExpectations)}</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              No active reporting template. Activate one in Federation settings to standardize required fields.
            </p>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
            <input className={inputClass} placeholder="Submission title" maxLength={240} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Summary</label>
            <textarea className={inputClass} rows={4} placeholder="Program update overview" maxLength={8000} value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))} required />
          </div>
          {templateFields.map((field) => (
            <div key={field.key}>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                {safeText(field.label || field.key)}{field.required ? ' *' : ''}
              </label>
              <textarea
                className={inputClass}
                rows={3}
                value={form.fieldValues[field.key] || ''}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  fieldValues: { ...f.fieldValues, [field.key]: e.target.value },
                }))}
                required={!!field.required}
              />
            </div>
          ))}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select
              className={inputClass}
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            >
              <option value="Submitted">{form.id ? 'Resubmit for review' : 'Submit now'}</option>
              <option value="Draft">Save as draft</option>
            </select>
          </div>
          <button className={btnPrimary} type="submit" disabled={saving}>
            {saving ? 'Saving…' : form.id
              ? (form.status === 'Draft' ? 'Save revised draft' : 'Resubmit program update')
              : (form.status === 'Draft' ? 'Save draft' : 'Submit program update')}
          </button>
        </form>
      )}
      {loading ? <LoadingBlock label="Loading submissions…" /> : (
        <ul className="space-y-3">
          {submissions.map((s) => (
            <li key={s.id} className="border border-navy-200 rounded-xl bg-white p-4">
              <div className="flex justify-between gap-3">
                <h2 className="font-semibold text-navy-900">{safeText(s.title)}</h2>
                <span className="text-xs text-gray-500">{safeText(s.status)}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {[s.programId && `Program ${s.programId.slice(0, 8)}`, s.teamId && `Team ${s.teamId.slice(0, 8)}`, s.reportingPeriodId && `Period ${s.reportingPeriodId.slice(0, 8)}`]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap mt-2">{safeText(s.summary)}</p>
              {s.leadershipNotes ? (
                <p className="text-sm text-amber-900 bg-amber-50 border border-amber-100 rounded mt-3 px-3 py-2 whitespace-pre-wrap">
                  Leadership notes: {safeText(s.leadershipNotes)}
                </p>
              ) : null}
              <p className="text-xs text-gray-500 mt-2">{safeText(s.submittedAt)}</p>
              {writable && isEditable(s.status) && (
                <div className="flex gap-2 mt-3">
                  <button type="button" className={btnPrimary} onClick={() => openRevise(s)}>
                    {s.status === 'Changes requested' ? 'Revise and resubmit' : 'Edit draft'}
                  </button>
                </div>
              )}
              {writable && isReviewable(s.status) && (
                <div className="mt-3 space-y-2">
                  <textarea
                    className={inputClass}
                    rows={2}
                    placeholder="Leadership notes (optional)"
                    value={reviewNotes[s.id] ?? s.leadershipNotes ?? ''}
                    onChange={(e) => setReviewNotes((prev) => ({ ...prev, [s.id]: e.target.value }))}
                  />
                  <div className="flex gap-2">
                    <button type="button" className={btnPrimary} onClick={() => reviewSubmission(s, 'Accepted')}>Accept</button>
                    <button type="button" className="border border-navy-300 text-navy-900 px-3 py-2 rounded text-sm" onClick={() => reviewSubmission(s, 'Changes requested')}>Request changes</button>
                  </div>
                </div>
              )}
            </li>
          ))}
          {!submissions.length && <li className="border border-navy-200 rounded-xl bg-white p-8 text-center text-sm text-gray-500">No program submissions yet.</li>}
        </ul>
      )}
    </>
  );
}

export default function FederationSubmissions() {
  return <FederationGate><SubmissionsInner /></FederationGate>;
}
